import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Calculator, ExternalLink, Maximize2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Cache-buster: bump this string whenever /dev/belzona.html is updated so the iframe reloads
const BELZONA_VERSION = '2026-05-27-06';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CRM Belzona Material Estimator
 * Embeds the standalone calculator at /dev/belzona.html via an iframe.
 * Parent React component fetches CRM projects (with the React app's working auth)
 * and pushes them into the iframe via postMessage — bypassing any iframe auth issues.
 */
const CRMBelzona = () => {
  const iframeRef = useRef(null);
  const [projectCount, setProjectCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const iframeSrc = `/dev/belzona.html?v=${BELZONA_VERSION}`;

  const apiFetch = async (path) => {
    const token = localStorage.getItem('bcs_session_token');
    const headers = { 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_URL}${path}`, { credentials: 'include', headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  };

  const fetchAndPushProjects = useCallback(async () => {
    setLoading(true);
    try {
      const all = [];
      let skip = 0;
      const PAGE = 200;
      // Paginate up to 25 pages (5000 projects)
      for (let i = 0; i < 25; i++) {
        const data = await apiFetch(`/api/crm/projects?limit=${PAGE}&skip=${skip}`);
        const batch = data.projects || [];
        all.push(...batch);
        if (batch.length < PAGE) break;
        skip += PAGE;
      }
      setProjectCount(all.length);
      // Push to iframe
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: 'BELZONA_CRM_PROJECTS', projects: all },
          window.location.origin
        );
      }
    } catch (e) {
      console.error('[CRMBelzona] Failed to load projects:', e);
      toast.error(`Failed to load CRM projects: ${e.message}`);
      setProjectCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen for the iframe announcing it's ready, then push projects in.
  // Also listen for the iframe's "refresh projects" request when user clicks ↻ Refresh inside.
  useEffect(() => {
    const handler = (event) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data) return;
      if (event.data.type === 'BELZONA_READY' || event.data.type === 'BELZONA_REQUEST_PROJECTS') {
        fetchAndPushProjects();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [fetchAndPushProjects]);

  // First push when the iframe loads (in case READY fired before listener was attached)
  const handleIframeLoad = () => {
    fetchAndPushProjects();
  };

  const openInNewTab = () => {
    window.open(iframeSrc, '_blank', 'noopener,noreferrer');
  };

  const goFullscreen = () => {
    const el = iframeRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  };

  return (
    <div className="space-y-4" data-testid="crm-belzona-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calculator className="text-indigo-600" size={26} />
            Belzona Material Estimator
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Calculate packages needed and total material cost for any Belzona repair.
            Pick a CRM project, then build the Bill of Materials.
            {projectCount !== null && (
              <span className="ml-2 text-indigo-600 font-semibold">
                · {projectCount} CRM project{projectCount !== 1 ? 's' : ''} loaded
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchAndPushProjects}
            disabled={loading}
            data-testid="belzona-reload-projects-btn"
            className="px-4 py-2 text-sm font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {loading ? 'Loading…' : 'Reload Projects'}
          </button>
          <button
            onClick={goFullscreen}
            data-testid="belzona-fullscreen-btn"
            className="px-4 py-2 text-sm font-semibold rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2"
          >
            <Maximize2 size={14} /> Fullscreen
          </button>
          <button
            onClick={openInNewTab}
            data-testid="belzona-newtab-btn"
            className="px-4 py-2 text-sm font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-2"
          >
            <ExternalLink size={14} /> Open in New Tab
          </button>
        </div>
      </div>

      <div className="rounded-lg overflow-hidden border border-slate-200 bg-white shadow-sm">
        <iframe
          ref={iframeRef}
          src={iframeSrc}
          title="Belzona Material Estimator"
          data-testid="belzona-iframe"
          onLoad={handleIframeLoad}
          className="w-full"
          style={{ height: 'calc(100vh - 280px)', minHeight: '700px', border: 'none' }}
        />
      </div>
    </div>
  );
};

export default CRMBelzona;
